import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Trophy, Users, Coins, Brain, ArrowsClockwise, Crown, Play, CheckCircle, Clock, MagicWand } from "@phosphor-icons/react";
import ImageUploader from "../components/ImageUploader";
import { extractVideoThumbnail, uploadBlob } from "../lib/videoThumbnail";

export default function CompetitionDetail() {
    const { id } = useParams();
    const { user, refresh } = useAuth();
    const [comp, setComp] = useState(null);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [videoUrl, setVideoUrl] = useState("");
    const [thumb, setThumb] = useState("");
    const [title, setTitle] = useState("");
    const [generatingThumb, setGeneratingThumb] = useState(false);

    const onGenEntryThumb = async () => {
        if (!videoUrl) return toast.error("Add a video URL first");
        try {
            setGeneratingThumb(true);
            const blob = await extractVideoThumbnail(videoUrl, { timeSec: 1 });
            const url = await uploadBlob(api, blob);
            setThumb(url);
            toast.success("Thumbnail generated");
        } catch {
            toast.error("Could not generate — video may not allow cross-origin access");
        } finally {
            setGeneratingThumb(false);
        }
    };

    const load = async () => {
        const [c, e] = await Promise.all([
            api.get(`/competitions/${id}`),
            api.get(`/competitions/${id}/entries`),
        ]);
        setComp(c.data);
        setEntries(e.data);
        setLoading(false);
    };

    useEffect(() => { load(); }, [id]);

    if (loading || !comp) return <div className="p-10 text-wta-muted">Loading…</div>;

    const myEntry = user && entries.find((e) => e.user_id === user.id);
    const isAdmin = user?.role === "admin";


    const onJoin = async () => {
        if (!user) return toast.error("Log in to join");
        try {
            await api.post(`/competitions/${id}/join`);
            toast.success(comp?.category === "trivia" ? "Joined! Wait for the tournament to start." : "Joined! Now submit your video.");
            await refresh();
            await load();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!videoUrl) return toast.error("Provide a video URL");
        setSubmitting(true);
        try {
            await api.post("/entries/submit", { competition_id: id, video_url: videoUrl, thumbnail: thumb, title });
            toast.success("Entry submitted");
            setVideoUrl(""); setThumb(""); setTitle("");
            await load();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    const onVote = async (entryId, paid) => {
        if (!user) return toast.error("Log in to vote");
        try {
            await api.post("/vote", { entry_id: entryId, paid });
            toast.success(paid ? "Paid vote cast (+1) — you can vote again" : "Vote cast");
            await refresh();
            await load();
        } catch (err) {
            const detail = formatErr(err.response?.data?.detail);
            if (err.response?.status === 403 && /face/i.test(detail || "")) {
                toast.error("Verify your face to vote");
                window.location.assign("/verify-face");
                return;
            }
            toast.error(detail);
        }
    };

    const startTournament = async () => {
        try {
            const { data } = await api.post(`/competitions/${id}/start`);
            toast.success(`Tournament started — ${data.matchups} matchups`);
            await load();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };
    const advanceRound = async () => {
        try {
            const { data } = await api.post(`/competitions/${id}/advance`);
            toast.success(data.finished ? "Tournament finished — winners crowned!" : `Advanced to round ${data.round}`);
            await load();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    return (
        <div className="px-4 md:px-10 py-8 max-w-6xl mx-auto">
            {/* Header */}
            <div className="grid md:grid-cols-2 gap-6 items-end">
                <div className="aspect-video bg-wta-surface2 border border-wta-border overflow-hidden rounded-sm">
                    {comp.cover_image && <img src={comp.cover_image} alt={comp.title} className="w-full h-full object-cover" />}
                </div>
                <div>
                    <div className="font-heading text-xs tracking-[0.4em] text-wta-accent">
                        {comp.category} · {comp.tier}
                        {comp.category === "modeling" && comp.gender && (
                            <span className={`ml-2 px-2 py-0.5 rounded-sm text-[10px] ${comp.gender === "female" ? "bg-pink-500/20 text-pink-300" : "bg-blue-500/20 text-blue-300"}`}>
                                {comp.gender === "female" ? "♀ FEMALE" : "♂ MALE"}
                            </span>
                        )}
                    </div>
                    <h1 className="font-heading text-4xl md:text-6xl tracking-tight uppercase mt-2 leading-none">{comp.title}</h1>
                    <p className="text-wta-muted mt-3">{comp.description}</p>
                    <div className="grid grid-cols-3 gap-2 mt-6">
                        <div className="bg-wta-surface border border-wta-border p-3 rounded-sm">
                            <div className="text-xs text-wta-muted tracking-widest uppercase">Entry</div>
                            <div className="font-heading text-2xl">${comp.entry_fee}</div>
                        </div>
                        <div className="bg-wta-surface border border-wta-border p-3 rounded-sm">
                            <div className="text-xs text-wta-muted tracking-widest uppercase">Pool</div>
                            <div className="font-heading text-2xl text-wta-accent">${comp.prize_pool.toFixed(0)}</div>
                        </div>
                        <div className="bg-wta-surface border border-wta-border p-3 rounded-sm">
                            <div className="text-xs text-wta-muted tracking-widest uppercase">Players</div>
                            <div className="font-heading text-2xl">{comp.entries_count}/32</div>
                        </div>
                    </div>
                    <div className="mt-6 flex flex-wrap gap-3">
                        {comp.status === "active" && comp.round_started_at && (
                            <RoundCountdown startedAt={comp.round_started_at} />
                        )}
                        {comp.status === "registering" && !myEntry && (
                            <button data-testid="join-comp-btn" onClick={onJoin} className="btn-primary px-6 py-3 rounded-sm text-base">
                                <Coins size={16} className="inline mr-1" /> Join · {comp.entry_fee * 100} coins
                            </button>
                        )}
                        {isAdmin && comp.status === "registering" && (
                            <button data-testid="admin-start-btn" onClick={startTournament} className="btn-ghost px-6 py-3 rounded-sm text-base font-heading uppercase tracking-wider">
                                Start Tournament
                            </button>
                        )}
                        {isAdmin && comp.status === "active" && (
                            <button data-testid="admin-advance-btn" onClick={advanceRound} className="btn-ghost px-6 py-3 rounded-sm text-base font-heading uppercase tracking-wider">
                                <ArrowsClockwise size={16} className="inline mr-1" /> Force Advance (override)
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Submission form (video comps only) */}
            {comp.category !== "trivia" && myEntry && myEntry.status !== "winner" && myEntry.status !== "eliminated" && (
                <div className="mt-10 bg-wta-surface border border-wta-border p-6 rounded-sm">
                    <div className="font-heading text-2xl tracking-wider mb-4">Your Entry</div>
                    <form onSubmit={onSubmit} className="grid md:grid-cols-2 gap-4">
                        <input data-testid="entry-title" placeholder="Performance title" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2.5 text-sm" />
                        <input data-testid="entry-thumbnail" placeholder="Thumbnail URL (or upload below)" value={thumb} onChange={(e) => setThumb(e.target.value)} className="bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2.5 text-sm" />
                        <input data-testid="entry-video-url" placeholder="Video URL (mp4 or hosted link)" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} className="md:col-span-2 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2.5 text-sm" />
                        <div className="md:col-span-2">
                            <label className="text-xs tracking-widest text-wta-muted uppercase block mb-2">Thumbnail (9:16)</label>
                            <div className="flex items-center gap-3 flex-wrap">
                                <ImageUploader
                                    value={thumb}
                                    onChange={setThumb}
                                    aspect={9 / 16}
                                    cropShape="rect"
                                    outputSize={{ width: 720, height: 1280 }}
                                    label="Upload Thumb"
                                    testid="entry-thumb-pic"
                                    previewClass="w-16 aspect-[9/16] rounded-sm"
                                    placeholder="—"
                                />
                                <button
                                    type="button"
                                    data-testid="entry-gen-thumb-btn"
                                    onClick={onGenEntryThumb}
                                    disabled={!videoUrl || generatingThumb}
                                    className="btn-ghost px-3 py-2 rounded-sm text-xs font-heading uppercase tracking-wider flex items-center gap-1 disabled:opacity-40"
                                >
                                    <MagicWand size={14} /> {generatingThumb ? "Generating…" : "Auto-Generate from Video"}
                                </button>
                            </div>
                        </div>
                        <button data-testid="entry-submit-btn" disabled={submitting} type="submit" className="md:col-span-2 btn-primary py-3 rounded-sm text-base disabled:opacity-50">
                            {submitting ? "Submitting…" : myEntry.video_url ? "Update Entry" : "Submit Entry"}
                        </button>
                    </form>
                </div>
            )}

            {/* Brackets */}
            {comp.brackets?.length > 0 && (
                <div className="mt-10">
                    <div className="font-heading text-3xl tracking-wider uppercase mb-4">Brackets</div>
                    <div className="overflow-x-auto pb-4">
                        <div className="flex gap-6 min-w-max">
                            {comp.brackets.map((round, ri) => (
                                <div key={ri} className="flex flex-col gap-3 min-w-[260px]">
                                    <div className="font-heading text-sm tracking-[0.3em] text-wta-muted uppercase">
                                        {ri + 1 === comp.brackets.length && comp.status === "finished" ? "Final" : `Round ${ri + 1}`}
                                    </div>
                                    {round.map((m, mi) => {
                                        const a = entries.find((e) => e.id === m.a);
                                        const b = entries.find((e) => e.id === m.b);
                                        return (
                                            <div key={mi} className={`bg-wta-surface border ${m.winner ? "bracket-winner" : "border-wta-border"} rounded-sm overflow-hidden`}>
                                                {[a, b].map((p, idx) => (
                                                    <div key={idx} className={`flex items-center justify-between p-2 text-sm ${m.winner === p?.id ? "bg-wta-primary/10 text-wta-primary" : ""} ${idx === 0 ? "border-b border-wta-border" : ""}`}>
                                                        <div className="truncate">{p ? `@${p.author?.name || "?"}` : <span className="text-wta-muted">— bye —</span>}</div>
                                                        <div className="text-xs text-wta-muted">{p?.votes_by_round?.[String(ri + 1)] ?? 0} votes</div>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Winners */}
            {comp.status === "finished" && comp.winners && (
                <div className="mt-10 bg-wta-surface border border-wta-accent p-6 rounded-sm">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="font-heading text-2xl tracking-wider uppercase flex items-center gap-2"><Crown size={24} weight="fill" className="text-wta-accent" /> Champions</div>
                        {comp.category !== "trivia" && (
                            <Link
                                to={`/competitions/${comp.id}/recap`}
                                data-testid="watch-recap-btn"
                                className="inline-flex items-center gap-2 btn-primary px-5 py-2.5 rounded-sm text-sm font-heading uppercase tracking-wider"
                            >
                                <Play size={14} weight="fill" /> Watch Recap
                            </Link>
                        )}
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 mt-4">
                        {[
                            { rank: "1st", id: comp.winners.first, prize: comp.winners.first_prize, color: "text-wta-accent" },
                            { rank: "2nd", id: comp.winners.second, prize: comp.winners.second_prize, color: "text-white" },
                            { rank: "3rd", id: comp.winners.third, prize: comp.winners.third_prize, color: "text-wta-muted" },
                        ].map((w) => {
                            const e = entries.find((x) => x.id === w.id);
                            return (
                                <div key={w.rank} className="bg-wta-surface2 border border-wta-border p-4 rounded-sm">
                                    <div className={`font-heading text-3xl ${w.color}`}>{w.rank}</div>
                                    <div className="text-sm mt-1">@{e?.author?.name || "—"}</div>
                                    <div className="font-heading text-2xl mt-1">${w.prize?.toFixed(2) ?? 0}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Active matchups vote view (video comps) */}
            {comp.status === "active" && comp.category !== "trivia" && (
                <div className="mt-10">
                    <div className="font-heading text-3xl tracking-wider uppercase mb-4">Vote — Round {comp.current_round}</div>
                    <div className="grid md:grid-cols-2 gap-4">
                        {(comp.brackets[comp.current_round - 1] || []).map((m, mi) => {
                            const a = entries.find((e) => e.id === m.a);
                            const b = entries.find((e) => e.id === m.b);
                            return (
                                <div key={mi} className="bg-wta-surface border border-wta-border rounded-sm p-4">
                                    <div className="text-xs tracking-widest uppercase text-wta-muted mb-3">Match {mi + 1}</div>
                                    <div className="grid grid-cols-2 gap-3">
                                        {[a, b].map((p, i) => p ? (
                                            <div key={i} className="flex flex-col">
                                                <div className="aspect-video bg-wta-surface2 border border-wta-border overflow-hidden rounded-sm">
                                                    {p.video_url ? (
                                                        <video src={p.video_url} poster={p.thumbnail || undefined} controls className="w-full h-full object-cover" data-testid={`entry-video-${p.id}`} />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-wta-muted text-xs">No video</div>
                                                    )}
                                                </div>
                                                <div className="mt-2 text-sm">@{p.author?.name}</div>
                                                <div className="text-xs text-wta-muted">{p.round_votes} votes</div>
                                                <div className="mt-2 flex gap-2">
                                                    <button data-testid={`vote-free-${p.id}`} onClick={() => onVote(p.id, false)} className="flex-1 btn-ghost py-2 px-3 rounded-sm text-xs font-heading uppercase tracking-wider">
                                                        Vote
                                                    </button>
                                                    <button data-testid={`vote-paid-${p.id}`} onClick={() => onVote(p.id, true)} className="flex-1 btn-primary py-2 px-3 rounded-sm text-xs">
                                                        +1 · 100c
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div key={i} className="flex items-center justify-center text-wta-muted text-xs aspect-video border border-wta-border rounded-sm">— bye —</div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Active trivia round (trivia comps) */}
            {comp.status === "active" && comp.category === "trivia" && (
                <TriviaRoundPanel comp={comp} entries={entries} myEntry={myEntry} />
            )}

            {/* Roster (registering) */}
            {comp.status === "registering" && (
                <div className="mt-10">
                    <div className="font-heading text-3xl tracking-wider uppercase mb-4">Contestants</div>
                    {entries.length === 0 ? (
                        <div className="text-wta-muted">No entries yet — be the first.</div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {entries.map((e) => (
                                <Link key={e.id} to={`/profile/${e.author?.id}`} className="bg-wta-surface border border-wta-border p-3 rounded-sm hover:border-wta-primary">
                                    <div className="text-sm">@{e.author?.name}</div>
                                    <div className="text-xs text-wta-muted">{e.status === "submitted" ? "Submitted" : "Registered"}</div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

const ROUND_DURATION_MS = 24 * 60 * 60 * 1000;

function TriviaRoundPanel({ comp, entries, myEntry }) {
    const curRound = comp.current_round;
    const matchups = comp.brackets?.[curRound - 1] || [];
    const myMatch = myEntry
        ? matchups.find((m) => m.a === myEntry.id || m.b === myEntry.id)
        : null;
    const opponentId = myMatch ? (myMatch.a === myEntry?.id ? myMatch.b : myMatch.a) : null;
    const opponent = opponentId ? entries.find((e) => e.id === opponentId) : null;
    const myScore = myEntry?.score_by_round?.[String(curRound)];
    const oppScore = opponent?.score_by_round?.[String(curRound)];
    const hasPlayed = myScore !== undefined && myScore !== null;
    const active = myEntry && myEntry.status !== "eliminated";

    return (
        <div className="mt-10">
            <div className="font-heading text-3xl tracking-wider uppercase mb-4">Round {curRound} — Trivia Duel</div>
            {!myEntry ? (
                <div className="bg-wta-surface border border-wta-border rounded-sm p-6 text-wta-muted">
                    You're not a participant in this tournament. Wait for the next registration window to join.
                </div>
            ) : !active ? (
                <div className="bg-wta-surface border border-wta-border rounded-sm p-6 text-wta-muted">
                    You've been eliminated from this tournament. Good luck next time.
                </div>
            ) : !myMatch ? (
                <div className="bg-wta-surface border border-wta-border rounded-sm p-6 text-wta-muted">
                    You got a bye this round — advance automatically.
                </div>
            ) : (
                <div className="bg-wta-surface border border-wta-border rounded-sm p-6">
                    <div className="grid grid-cols-2 gap-4 items-center">
                        <div>
                            <div className="text-xs tracking-widest uppercase text-wta-muted">You</div>
                            <div className="font-heading text-2xl">@{myEntry.author?.name}</div>
                            <div className={`font-heading text-4xl mt-2 ${hasPlayed ? "text-wta-accent" : "text-wta-muted"}`} data-testid="my-trivia-score">
                                {hasPlayed ? myScore : "—"}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs tracking-widest uppercase text-wta-muted">Opponent</div>
                            <div className="font-heading text-2xl">{opponent ? `@${opponent.author?.name}` : "— bye —"}</div>
                            <div className={`font-heading text-4xl mt-2 ${oppScore !== undefined && oppScore !== null ? "text-white" : "text-wta-muted"}`} data-testid="opponent-trivia-score">
                                {oppScore !== undefined && oppScore !== null ? oppScore : "—"}
                            </div>
                        </div>
                    </div>
                    <div className="mt-6">
                        {hasPlayed ? (
                            <div className="flex items-center gap-2 text-sm text-wta-accent">
                                <CheckCircle size={18} weight="fill" />
                                Round played — waiting for opponent / auto-advance.
                            </div>
                        ) : (
                            <Link
                                to={`/competitions/${comp.id}/trivia/play`}
                                data-testid="play-trivia-round-btn"
                                className="inline-flex items-center gap-2 btn-primary px-6 py-3 rounded-sm text-base"
                            >
                                <Play size={16} weight="fill" /> Play Round {curRound}
                            </Link>
                        )}
                    </div>
                    <div className="mt-3 text-xs text-wta-muted flex items-center gap-2">
                        <Clock size={14} /> Highest score wins. Round auto-resolves after the timer.
                    </div>
                </div>
            )}
        </div>
    );
}

function RoundCountdown({ startedAt }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(t);
    }, []);
    const start = new Date(startedAt).getTime();
    const remaining = Math.max(0, start + ROUND_DURATION_MS - now);
    const h = Math.floor(remaining / 3_600_000);
    const m = Math.floor((remaining % 3_600_000) / 60_000);
    const label = remaining === 0
        ? "Auto-advancing soon"
        : `Round auto-advances in ${h}h ${m}m`;
    return (
        <div className="px-3 py-2 rounded-sm border border-wta-accent/40 text-wta-accent text-xs font-heading uppercase tracking-wider flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-wta-accent live-pulse" />
            {label}
        </div>
    );
}

